import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { useNavigate } from 'react-router-dom';
import PageShell from './components/PageShell';
import Snackbar from './components/Snackbar';
import ContractLifecycleBar from './components/ContractLifecycleBar';
import {
  filterSubscriptionCustomers,
  legacyCustomerApi,
  subscriptionAdminApi,
  getApiErrorMessage,
} from '../subscriptionAdmin/api';
import { canWrite } from '../subscriptionAdmin/permissions';
import {
  ContractBillingPreview,
  formatContractBillingDisplay,
  GST_MODE_OPTIONS,
} from '../subscriptionAdmin/gstUtils';
import {
  planFinalPrice,
  planHours,
  planValidityMonths,
  planCommitmentMonths,
  planFreeMonths,
} from '../subscriptionAdmin/planUtils';

const STATUS_COLORS = {
  draft: 'default',
  pending_approval: 'warning',
  approved: 'info',
  pending_payment: 'warning',
  active: 'success',
  suspended: 'error',
  cancelled: 'default',
  terminated: 'error',
  renewal_pending: 'info',
  expired: 'default',
};

/** MUI Select inside Dialog: menu must render in-dialog or clicks do not register. */
const DIALOG_SELECT_PROPS = {
  MenuProps: {
    disablePortal: true,
    PaperProps: { sx: { maxHeight: 280 } },
  },
};

function toId(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'object') return String(value._id || value.id || '');
  return String(value);
}

/** Add calendar months to a YYYY-MM-DD start date (local, for date inputs). */
function addMonthsToDateString(startDate, months) {
  if (!startDate) return '';
  const [y, m, d] = startDate.split('-').map(Number);
  if (!y || !m || !d) return '';
  const date = new Date(y, m - 1, d);
  date.setMonth(date.getMonth() + months);
  const yy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

function todayDateString() {
  const now = new Date();
  const yy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

/** UTC midnight ISO — avoids local timezone shifting the calendar day. */
function dateInputToIso(dateStr) {
  if (!dateStr) return undefined;
  return `${dateStr}T00:00:00.000Z`;
}

function formatScheduleDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function billingAnchorDayFromDate(dateStr) {
  if (!dateStr) return 1;
  const day = Number(dateStr.split('-')[2]);
  return Number.isFinite(day) && day >= 1 && day <= 28 ? day : 1;
}

function resolveScheduleEndDate(startDate, validityMonths) {
  if (!startDate || !validityMonths) return '';
  return addMonthsToDateString(startDate, validityMonths);
}

function selectedPlan(plans, planId) {
  return plans.find((p) => toId(p) === planId) || null;
}

function contractUserId(contract) {
  const u = contract?.userId;
  if (!u) return '';
  if (typeof u === 'object') return String(u._id || u.id || '');
  return String(u);
}

function customerDisplayName(customer) {
  return customer?.name || customer?.organizationName || '';
}

function customerOptionLabel(customer) {
  const name = customerDisplayName(customer);
  const email = customer?.email || '';
  if (name && email) return `${name} (${email})`;
  return email || name || toId(customer);
}

function formatStatusLabel(status) {
  if (!status) return 'Unknown';
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function ContractMetric({ icon: Icon, label, value, accent, subValue }) {
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        bgcolor: accent ? `${accent}08` : 'rgba(15,23,42,0.03)',
        border: '1px solid',
        borderColor: accent ? `${accent}20` : 'rgba(15,23,42,0.06)',
        height: '100%',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <Icon sx={{ fontSize: 16, color: accent || '#64748b' }} />
        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, letterSpacing: '0.02em' }}>
          {label}
        </Typography>
      </Stack>
      <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
        {value}
      </Typography>
      {subValue ? (
        <Typography variant="caption" sx={{ display: 'block', color: '#64748b', mt: 0.25, lineHeight: 1.35 }}>
          {subValue}
        </Typography>
      ) : null}
    </Box>
  );
}

function ContractCard({ contract, assigned, canEdit, pendingByContract, onAction }) {
  const contractId = String(contract._id || '').slice(-8);
  const channel = contract.billing?.paymentChannel || 'online';
  const isOffline = channel === 'offline';
  const billingDisplay = formatContractBillingDisplay(contract.billing);

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid rgba(15,23,42,0.08)',
        borderRadius: 3,
        overflow: 'hidden',
        transition: 'box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease',
        '&:hover': {
          boxShadow: '0 12px 32px rgba(15,23,42,0.08)',
          borderColor: 'rgba(15,23,42,0.12)',
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 2,
          background: 'linear-gradient(135deg, rgba(15,23,42,0.03) 0%, rgba(15,23,42,0.01) 100%)',
          borderBottom: '1px solid rgba(15,23,42,0.06)',
        }}
      >
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
          <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ minWidth: 0, flex: 1 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                flexShrink: 0,
              }}
            >
              <DescriptionOutlinedIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.3 }}
                noWrap
              >
                {assigned.primary}
              </Typography>
              {assigned.secondary && (
                <Typography variant="body2" color="text.secondary" noWrap>
                  {assigned.secondary}
                </Typography>
              )}
              <Typography
                variant="caption"
                sx={{
                  display: 'inline-block',
                  mt: 0.75,
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  bgcolor: 'rgba(15,23,42,0.06)',
                  fontFamily: 'monospace',
                  color: '#64748b',
                }}
              >
                #{contractId}
              </Typography>
            </Box>
          </Stack>
          <Chip
            label={formatStatusLabel(contract.status)}
            size="small"
            color={STATUS_COLORS[contract.status] || 'default'}
            sx={{ fontWeight: 600, flexShrink: 0 }}
          />
        </Stack>
      </Box>

      <CardContent sx={{ flex: 1, px: 2.5, py: 2, '&:last-child': { pb: 2 } }}>
        <Grid container spacing={1.5}>
          <Grid item xs={6}>
            <ContractMetric
              icon={PaymentsOutlinedIcon}
              label="Amount due"
              value={billingDisplay.primary}
              subValue={billingDisplay.secondary}
              accent="#2563eb"
            />
          </Grid>
          <Grid item xs={6}>
            <ContractMetric
              icon={PersonOutlineOutlinedIcon}
              label="Term"
              value={contract.commitmentMonths != null ? `${contract.commitmentMonths} months` : '—'}
              accent="#7c3aed"
            />
          </Grid>
          <Grid item xs={12}>
            <ContractMetric
              icon={CalendarTodayOutlinedIcon}
              label="Agreement period"
              value={`${formatScheduleDate(contract.schedule?.startDate)} → ${formatScheduleDate(contract.schedule?.endDate)}`}
              accent="#059669"
            />
          </Grid>
        </Grid>

        <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
          <Chip
            label={isOffline ? 'Offline payment' : 'Online (Razorpay)'}
            size="small"
            color={isOffline ? 'warning' : 'default'}
            variant="outlined"
            sx={{ fontWeight: 500 }}
          />
          {contract.billing?.cycle && (
            <Chip
              label={`${contract.billing.cycle} billing`}
              size="small"
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
          )}
          {contract.billing?.gstMode && (
            <Chip
              label={`GST ${contract.billing.gstRate ?? 18}% (${contract.billing.gstMode})`}
              size="small"
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
          )}
        </Stack>
      </CardContent>

      {canEdit && (
        <>
          <Divider sx={{ borderColor: 'rgba(15,23,42,0.06)' }} />
          <Box
            sx={{
              px: 2.5,
              py: 2,
              bgcolor: 'rgba(248,250,252,0.8)',
            }}
          >
            <Typography
              variant="overline"
              sx={{ color: '#64748b', fontWeight: 700, letterSpacing: '0.08em', display: 'block', mb: 1.25 }}
            >
              Lifecycle actions
            </Typography>
            <ContractLifecycleBar
              contract={contract}
              pendingByContract={pendingByContract}
              onAction={onAction}
              layout="card"
            />
          </Box>
        </>
      )}
    </Card>
  );
}

function resolveAssignedCustomer(contract, customerById) {
  const id = contractUserId(contract);
  const customer = id ? customerById.get(id) : null;
  const source = customer || (typeof contract?.userId === 'object' ? contract.userId : null);

  if (source) {
    const name = customerDisplayName(source);
    const email = source.email || '';
    return {
      primary: name || email || id,
      secondary: name && email ? email : null,
    };
  }

  return {
    primary: id ? `Unknown user (${id.slice(-8)})` : '—',
    secondary: null,
  };
}

const emptyContractForm = () => {
  const startDate = todayDateString();
  return {
    userId: '',
    subscriptionPlanId: '',
    cycle: 'monthly',
    paymentChannel: 'online',
    amount: '',
    gstMode: 'exclusive',
    gstRate: 18,
    hoursPerMonth: '',
    commitmentMonths: '',
    startDate,
    endDate: '',
    endDateManual: false,
  };
};

const MANUAL_PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank transfer' },
  { value: 'cheque', label: 'Cheque' },
];

export default function ContractsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const prefillUserId = location.state?.prefillUserId || '';
  const [contracts, setContracts] = useState([]);
  const [plans, setPlans] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyContractForm());
  const [saving, setSaving] = useState(false);
  const [pendingByContract, setPendingByContract] = useState({});
  const pendingContractIdsRef = useRef(new Set());
  const [offlineDialog, setOfflineDialog] = useState({ open: false, contract: null, mode: null });
  const [offlineForm, setOfflineForm] = useState({ note: '', reference: '' });
  const [manualForm, setManualForm] = useState({
    paymentMethod: 'cash',
    amount: '',
    reference: '',
    note: '',
    receivedAt: '',
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', type: 'error' });
  const canEdit = canWrite('contracts');

  const customerById = useMemo(() => {
    const map = new Map();
    customers.forEach((u) => {
      const id = toId(u);
      if (id) map.set(id, u);
    });
    return map;
  }, [customers]);

  const sortedCustomers = useMemo(
    () =>
      [...customers].sort((a, b) =>
        customerOptionLabel(a).localeCompare(customerOptionLabel(b), undefined, { sensitivity: 'base' })
      ),
    [customers]
  );

  const filteredContracts = useMemo(() => {
    if (!userFilter) return contracts;
    return contracts.filter((c) => contractUserId(c) === userFilter);
  }, [contracts, userFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter ? { status: statusFilter } : undefined;
      const [contractData, planData, userData] = await Promise.all([
        subscriptionAdminApi.listContracts(params),
        subscriptionAdminApi.listPlans({ planCategory: 'main', status: 'active' }),
        legacyCustomerApi.listUsers(),
      ]);
      setContracts(Array.isArray(contractData) ? contractData : []);
      setPlans(Array.isArray(planData) ? planData : []);
      setCustomers(filterSubscriptionCustomers(userData));
    } catch (err) {
      setSnackbar({ open: true, type: 'error', message: getApiErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (prefillUserId) {
      const id = toId(prefillUserId);
      setUserFilter(id);
      setForm((f) => ({ ...emptyContractForm(), userId: id }));
      setDialogOpen(true);
    }
  }, [prefillUserId]);

  const runAction = async (contract, action) => {
    const contractId = toId(contract);
    if (!contractId || pendingContractIdsRef.current.has(contractId)) return;

    if (action.dialog === 'offlineActivate') {
      setOfflineForm({ note: '', reference: '' });
      setOfflineDialog({ open: true, contract, mode: 'offlineActivate' });
      return;
    }
    if (action.dialog === 'manualPayment') {
      setManualForm({
        paymentMethod: 'cash',
        amount: contract.billing?.amount != null ? String(contract.billing.amount) : '',
        reference: '',
        note: '',
        receivedAt: '',
      });
      setOfflineDialog({ open: true, contract, mode: 'manualPayment' });
      return;
    }

    pendingContractIdsRef.current.add(contractId);
    setPendingByContract((prev) => ({ ...prev, [contractId]: action.key }));
    try {
      await action.fn(contract._id);
      setSnackbar({ open: true, type: 'success', message: `${action.label} completed.` });
      await load();
    } catch (err) {
      setSnackbar({ open: true, type: 'error', message: getApiErrorMessage(err) });
    } finally {
      pendingContractIdsRef.current.delete(contractId);
      setPendingByContract((prev) => {
        if (!prev[contractId]) return prev;
        const next = { ...prev };
        delete next[contractId];
        return next;
      });
    }
  };

  const submitOfflineDialog = async () => {
    const contract = offlineDialog.contract;
    const contractId = toId(contract);
    if (!contractId) return;

    pendingContractIdsRef.current.add(contractId);
    setPendingByContract((prev) => ({
      ...prev,
      [contractId]: offlineDialog.mode === 'offlineActivate' ? 'activate-offline' : 'record-payment',
    }));

    try {
      if (offlineDialog.mode === 'offlineActivate') {
        if (!offlineForm.note.trim()) {
          setSnackbar({ open: true, type: 'error', message: 'Note is required for offline activation.' });
          pendingContractIdsRef.current.delete(contractId);
          setPendingByContract((prev) => {
            if (!prev[contractId]) return prev;
            const next = { ...prev };
            delete next[contractId];
            return next;
          });
          return;
        }
        await subscriptionAdminApi.activateContractOffline(contractId, {
          note: offlineForm.note.trim(),
          reference: offlineForm.reference.trim() || undefined,
        });
        setSnackbar({ open: true, type: 'success', message: 'Offline activation recorded.' });
      } else {
        await subscriptionAdminApi.recordContractPayment(contractId, {
          paymentMethod: manualForm.paymentMethod,
          amount: manualForm.amount !== '' ? Number(manualForm.amount) : undefined,
          reference: manualForm.reference.trim() || undefined,
          note: manualForm.note.trim() || undefined,
          receivedAt: manualForm.receivedAt
            ? new Date(manualForm.receivedAt).toISOString()
            : undefined,
        });
        setSnackbar({ open: true, type: 'success', message: 'Manual payment recorded.' });
      }
      setOfflineDialog({ open: false, contract: null, mode: null });
      await load();
    } catch (err) {
      setSnackbar({ open: true, type: 'error', message: getApiErrorMessage(err) });
    } finally {
      pendingContractIdsRef.current.delete(contractId);
      setPendingByContract((prev) => {
        if (!prev[contractId]) return prev;
        const next = { ...prev };
        delete next[contractId];
        return next;
      });
    }
  };

  const onPlanChange = (planId) => {
    const plan = selectedPlan(plans, planId);
    const validity = plan ? planValidityMonths(plan) : null;
    const commitment = plan ? planCommitmentMonths(plan) : '';
    setForm((f) => {
      const startDate = f.startDate || todayDateString();
      const endDate = f.endDateManual
        ? f.endDate
        : resolveScheduleEndDate(startDate, validity);
      return {
        ...f,
        subscriptionPlanId: planId,
        amount: plan ? String(planFinalPrice(plan) ?? '') : f.amount,
        hoursPerMonth: plan ? String(planHours(plan)) : f.hoursPerMonth,
        commitmentMonths: commitment !== '' ? String(commitment) : '',
        startDate,
        endDate,
      };
    });
  };

  const onStartDateChange = (startDate) => {
    const plan = selectedPlan(plans, form.subscriptionPlanId);
    const validity = plan ? planValidityMonths(plan) : null;
    setForm((f) => ({
      ...f,
      startDate,
      endDate: f.endDateManual ? f.endDate : resolveScheduleEndDate(startDate, validity),
    }));
  };

  const resetEndDateFromPlan = () => {
    const plan = selectedPlan(plans, form.subscriptionPlanId);
    const validity = plan ? planValidityMonths(plan) : null;
    setForm((f) => ({
      ...f,
      endDate: resolveScheduleEndDate(f.startDate, validity),
      endDateManual: false,
    }));
  };

  const formPlan = selectedPlan(plans, form.subscriptionPlanId);
  const formValidityMonths = formPlan ? planValidityMonths(formPlan) : null;
  const formFreeMonths = formPlan ? planFreeMonths(formPlan) : null;

  const createContract = async () => {
    if (!form.userId || !form.subscriptionPlanId) {
      setSnackbar({ open: true, type: 'error', message: 'Customer and plan are required.' });
      return;
    }
    setSaving(true);
    try {
      await subscriptionAdminApi.createContract({
        userId: form.userId,
        subscriptionPlanId: form.subscriptionPlanId,
        commitmentMonths: Number(form.commitmentMonths) || undefined,
        billing: {
          cycle: form.cycle,
          amount: Number(form.amount) || undefined,
          currency: 'INR',
          gstMode: form.gstMode,
          gstRate: Number(form.gstRate),
          paymentChannel: form.paymentChannel,
        },
        schedule: {
          startDate: dateInputToIso(form.startDate),
          endDate: dateInputToIso(form.endDate),
          billingAnchorDay: billingAnchorDayFromDate(form.startDate),
        },
        terms: {
          hoursPerMonth: Number(form.hoursPerMonth) || undefined,
          addonAllowed: true,
        },
      });
      setSnackbar({ open: true, type: 'success', message: 'Contract draft created. Submit → approve → payment.' });
      setDialogOpen(false);
      load();
    } catch (err) {
      setSnackbar({ open: true, type: 'error', message: getApiErrorMessage(err) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageShell
      title="Step 2 — Contracts"
      subtitle="Lifecycle: submit → approve → send-for-payment → pay (step 4) → active"
      actions={
        canEdit ? (
          <Button
            variant="contained"
            disableElevation
            sx={{ borderRadius: 2, px: 2.5, fontWeight: 600, textTransform: 'none' }}
            onClick={() => {
              setForm({
                ...emptyContractForm(),
                ...(prefillUserId ? { userId: toId(prefillUserId) } : {}),
              });
              setDialogOpen(true);
            }}
          >
            New contract
          </Button>
        ) : null
      }
    >
      <Alert
        severity="info"
        sx={{
          mb: 3,
          borderRadius: 2.5,
          border: '1px solid rgba(37,99,235,0.15)',
          bgcolor: 'rgba(37,99,235,0.04)',
        }}
      >
        Typical order: <strong>Submit</strong> → <strong>Approve</strong> → <strong>Send for payment</strong> → take payment in{' '}
        <Button size="small" onClick={() => navigate('/admin-subscriptions/payment-setup')}>Step 4</Button>
        {' '}for online contracts, or <strong>Activate offline</strong> / <strong>Record payment</strong> for offline contracts.
        Use <strong>Activate</strong> only when activating without payment (online, admin override).
      </Alert>

      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: 2,
          borderRadius: 2.5,
          border: '1px solid rgba(15,23,42,0.08)',
          bgcolor: 'white',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a' }}>
            {loading
              ? 'Loading contracts…'
              : `${filteredContracts.length} contract${filteredContracts.length === 1 ? '' : 's'}${
                  userFilter ? ' for selected customer' : ''
                }`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {userFilter
              ? 'Showing contracts for the selected customer only.'
              : 'Manage lifecycle actions from each card below.'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ alignItems: 'center' }}>
          <TextField
            select
            size="small"
            label="Select customer"
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            sx={{ minWidth: 260 }}
          >
            <MenuItem value="">All customers</MenuItem>
            {sortedCustomers.map((u) => {
              const id = toId(u);
              if (!id) return null;
              return (
                <MenuItem key={id} value={id}>
                  {customerOptionLabel(u)}
                </MenuItem>
              );
            })}
          </TextField>
          <TextField
            select
            size="small"
            label="Filter by status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">All statuses</MenuItem>
            {['draft', 'pending_approval', 'approved', 'pending_payment', 'active', 'suspended', 'renewal_pending', 'cancelled', 'terminated'].map((s) => (
              <MenuItem key={s} value={s}>{formatStatusLabel(s)}</MenuItem>
            ))}
          </TextField>
        </Stack>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
      ) : filteredContracts.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            py: 8,
            px: 3,
            textAlign: 'center',
            borderRadius: 3,
            border: '1px dashed rgba(15,23,42,0.12)',
            bgcolor: 'white',
          }}
        >
          <DescriptionOutlinedIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', mb: 1 }}>
            {userFilter ? 'No contracts for this customer' : 'No contracts yet'}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3, maxWidth: 420, mx: 'auto' }}>
            {userFilter
              ? 'This customer has no contracts matching the current filters. Try another customer or clear the selection.'
              : 'Create a draft after completing steps 0 and 1. Contracts will appear here as cards with lifecycle actions.'}
          </Typography>
          {userFilter ? (
            <Button variant="outlined" onClick={() => setUserFilter('')}>
              Show all customers
            </Button>
          ) : (
            canEdit && (
              <Button
                variant="contained"
                onClick={() => {
                  setForm({
                    ...emptyContractForm(),
                    ...(prefillUserId ? { userId: toId(prefillUserId) } : {}),
                  });
                  setDialogOpen(true);
                }}
              >
                Create first contract
              </Button>
            )
          )}
        </Paper>
      ) : (
        <Grid container spacing={2.5}>
          {filteredContracts.map((c) => {
            const assigned = resolveAssignedCustomer(c, customerById);
            return (
              <Grid item xs={12} md={6} xl={4} key={c._id}>
                <ContractCard
                  contract={c}
                  assigned={assigned}
                  canEdit={canEdit}
                  pendingByContract={pendingByContract}
                  onAction={(action) => runAction(c, action)}
                />
              </Grid>
            );
          })}
        </Grid>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create contract (draft)</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            select
            label="Customer"
            value={form.userId}
            onChange={(e) => setForm({ ...form, userId: e.target.value })}
            SelectProps={DIALOG_SELECT_PROPS}
            required
            fullWidth
          >
            <MenuItem value="">Select…</MenuItem>
            {customers.map((u) => {
              const id = toId(u);
              if (!id) return null;
              return (
                <MenuItem key={id} value={id}>
                  {u.email || u.name || id}
                </MenuItem>
              );
            })}
          </TextField>
          <TextField
            select
            label="Subscription plan"
            value={form.subscriptionPlanId}
            onChange={(e) => onPlanChange(e.target.value)}
            SelectProps={DIALOG_SELECT_PROPS}
            required
            fullWidth
          >
            <MenuItem value="">Select…</MenuItem>
            {plans.map((p) => {
              const id = toId(p);
              if (!id) return null;
              return (
                <MenuItem key={id} value={id}>
                  {p.planName}
                </MenuItem>
              );
            })}
          </TextField>
          <TextField
            label="Billing amount (INR, after discount)"
            type="number"
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            helperText={
              form.gstMode === 'inclusive'
                ? 'Taxable value is derived from this gross amount.'
                : 'GST is calculated on top of this amount.'
            }
            fullWidth
          />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <TextField
              select
              label="GST mode"
              value={form.gstMode}
              onChange={(e) => setForm({ ...form, gstMode: e.target.value })}
              SelectProps={DIALOG_SELECT_PROPS}
              helperText="Whether billing amount includes GST or not."
              sx={{ flex: 1, minWidth: 200 }}
            >
              {GST_MODE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="GST rate (%)"
              type="number"
              value={form.gstRate}
              onChange={(e) => setForm({ ...form, gstRate: e.target.value })}
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              sx={{ flex: 1, minWidth: 120 }}
            />
          </Box>
          <ContractBillingPreview
            amount={form.amount}
            gstMode={form.gstMode}
            gstRate={form.gstRate}
          />
          <TextField
            select
            label="Payment channel"
            value={form.paymentChannel}
            onChange={(e) => setForm({ ...form, paymentChannel: e.target.value })}
            SelectProps={DIALOG_SELECT_PROPS}
            fullWidth
          >
            <MenuItem value="online">Online (Razorpay)</MenuItem>
            <MenuItem value="offline">Offline (cash / bank / cheque)</MenuItem>
          </TextField>
          <TextField label="Hours per month" type="number" value={form.hoursPerMonth} onChange={(e) => setForm({ ...form, hoursPerMonth: e.target.value })} />

          <Alert severity="info" sx={{ py: 0.5 }}>
            <Typography variant="body2" component="div">
              <strong>Billing commitment</strong> ({form.commitmentMonths || '—'} months) controls how many paid/free billing cycles apply.
              <br />
              <strong>Agreement dates</strong> define the full contract calendar
              {formValidityMonths ? ` (${formValidityMonths} months from plan)` : ''}.
              {formFreeMonths != null ? ` Plan includes ${formFreeMonths} free month(s) for new users.` : ''}
            </Typography>
          </Alert>

          <TextField
            label="Billing commitment (months)"
            type="number"
            value={form.commitmentMonths}
            onChange={(e) => setForm({ ...form, commitmentMonths: e.target.value })}
            helperText="Sent as commitmentMonths — defaults from plan. Drives paid/free billing cycles."
            fullWidth
            inputProps={{ min: 1 }}
          />

          <TextField
            label="Agreement start date"
            type="date"
            value={form.startDate}
            onChange={(e) => onStartDateChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText="When the contract begins. Backend uses this for nextBillDate."
            fullWidth
          />

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            <TextField
              label="Agreement end date"
              type="date"
              value={form.endDate}
              onChange={(e) => setForm({ ...form, endDate: e.target.value, endDateManual: true })}
              InputLabelProps={{ shrink: true }}
              helperText={
                form.endDateManual
                  ? 'Custom end date (overrides plan duration).'
                  : `Auto: start + ${formValidityMonths ?? '?'} months (plan validity).`
              }
              sx={{ flex: 1, minWidth: 200 }}
            />
            <Button
              size="small"
              variant="outlined"
              onClick={resetEndDateFromPlan}
              disabled={!form.subscriptionPlanId || !form.startDate}
              sx={{ mt: 0.5 }}
            >
              Reset from plan
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={createContract} disabled={saving}>Create draft</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={offlineDialog.open}
        onClose={() => setOfflineDialog({ open: false, contract: null, mode: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {offlineDialog.mode === 'offlineActivate' ? 'Activate offline' : 'Record manual payment'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {offlineDialog.mode === 'offlineActivate' ? (
            <>
              <Alert severity="info">
                Records a cash payment and activates the contract. Requires billing.paymentChannel = offline.
              </Alert>
              <TextField
                label="Note"
                value={offlineForm.note}
                onChange={(e) => setOfflineForm({ ...offlineForm, note: e.target.value })}
                required
                fullWidth
                multiline
                minRows={2}
              />
              <TextField
                label="Reference (optional)"
                value={offlineForm.reference}
                onChange={(e) => setOfflineForm({ ...offlineForm, reference: e.target.value })}
                fullWidth
              />
            </>
          ) : (
            <>
              <Alert severity="info">
                Record cash, bank transfer, or cheque for the current billing cycle on an offline contract.
              </Alert>
              <TextField
                select
                label="Payment method"
                value={manualForm.paymentMethod}
                onChange={(e) => setManualForm({ ...manualForm, paymentMethod: e.target.value })}
                fullWidth
              >
                {MANUAL_PAYMENT_METHODS.map((m) => (
                  <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="Amount (INR)"
                type="number"
                value={manualForm.amount}
                onChange={(e) => setManualForm({ ...manualForm, amount: e.target.value })}
                fullWidth
                helperText="Leave blank to use the expected contract charge."
              />
              <TextField
                label="Reference (optional)"
                value={manualForm.reference}
                onChange={(e) => setManualForm({ ...manualForm, reference: e.target.value })}
                fullWidth
              />
              <TextField
                label="Note (optional)"
                value={manualForm.note}
                onChange={(e) => setManualForm({ ...manualForm, note: e.target.value })}
                fullWidth
              />
              <TextField
                label="Received at"
                type="datetime-local"
                value={manualForm.receivedAt}
                onChange={(e) => setManualForm({ ...manualForm, receivedAt: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOfflineDialog({ open: false, contract: null, mode: null })}>Cancel</Button>
          <Button variant="contained" onClick={submitOfflineDialog}>
            {offlineDialog.mode === 'offlineActivate' ? 'Activate' : 'Record payment'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar {...snackbar} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} />
    </PageShell>
  );
}
